import React from 'react';
import styles from './RecipeDietaryAttributes.module.css';
import { getDietaryTooltip } from '../../utils/dietaryTooltips';

interface RecipeDietaryAttributesProps {
  dietaryAttributes: {
    vegan?: boolean;
    vegetarian?: boolean;
    pescatarian?: boolean;
    gluten_free?: boolean;
    dairy_free?: boolean;
    nut_free?: boolean;
    egg_free?: boolean;
    soy_free?: boolean;
    shellfish_free?: boolean;
    fish_free?: boolean;
    sesame_free?: boolean;
    low_fodmap?: boolean;
    keto?: boolean;
    paleo?: boolean;
    atkins?: boolean;
    corn_free?: boolean;
    mustard_free?: boolean;
  };
  userPreferences?: {
    vegan?: boolean;
    vegetarian?: boolean;
    pescatarian?: boolean;
    gluten_free?: boolean;
    dairy_free?: boolean;
    nut_free?: boolean;
    egg_free?: boolean;
    soy_free?: boolean;
    shellfish_free?: boolean;
    fish_free?: boolean;
    sesame_free?: boolean;
    low_fodmap?: boolean;
    keto?: boolean;
    paleo?: boolean;
    atkins?: boolean;
    corn_free?: boolean;
    mustard_free?: boolean;
  };
  variant?: 'default' | 'compact' | 'inline';
  showOnlyMatches?: boolean;
  maxTags?: number;
  className?: string;
}

const RecipeDietaryAttributes: React.FC<RecipeDietaryAttributesProps> = ({
  dietaryAttributes,
  userPreferences,
  variant = 'default',
  showOnlyMatches = true,
  maxTags,
  className
}) => {
  if (!dietaryAttributes) return null;

  const dietaryMapping = {
    'vegan': 'Vegan',
    'vegetarian': 'Vegetarian',
    'pescatarian': 'Pescatarian',
    'gluten_free': 'Gluten Allergy',
    'dairy_free': 'Dairy Allergy',
    'nut_free': 'Nut Allergy',
    'egg_free': 'Egg Allergy',
    'soy_free': 'Soy Allergy',
    'shellfish_free': 'Shellfish Allergy',
    'fish_free': 'Fish Allergy',
    'sesame_free': 'Sesame Allergy',
    'low_fodmap': 'Low FODMAP',
    'keto': 'Keto',
    'paleo': 'Paleo',
    'atkins': 'Atkins',
    'corn_free': 'Corn Allergy',
    'mustard_free': 'Mustard Allergy'
  };

  // Get all dietary attributes that are true for the recipe
  const recipeAttributes = Object.entries(dietaryAttributes)
    .filter(([key, value]) => value === true)
    .map(([key, value]) => ({
      key,
      label: dietaryMapping[key as keyof typeof dietaryMapping],
      isUserPreference: userPreferences ? userPreferences[key as keyof typeof userPreferences] === true : false,
      isRecipeMatch: true
    }));

  // Filter to only show matches if requested
  const displayAttributes = showOnlyMatches && userPreferences
    ? recipeAttributes.filter(attr => attr.isUserPreference)
    : recipeAttributes;

  if (displayAttributes.length === 0) return null;

  // Apply max tags limit
  const finalAttributes = maxTags 
    ? displayAttributes.slice(0, maxTags)
    : displayAttributes;

  const remainingCount = maxTags && displayAttributes.length > maxTags
    ? displayAttributes.length - maxTags
    : 0;

  return (
    <div className={`${styles.container} ${styles[variant]} ${className || ''}`}>
      {finalAttributes.map(({ key, label, isUserPreference }) => (
        <span
          key={key}
          className={`${styles.dietaryTag} ${
            isUserPreference ? styles.userMatch : styles.recipeAttribute
          } ${styles[key]}`}
          title={getDietaryTooltip(key, isUserPreference, true)}
        >
          {label}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className={styles.moreTags}>
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

export default RecipeDietaryAttributes;
